import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Row, Col, List, Typography } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, SisternodeOutlined, RocketOutlined, RocketTwoTone } from '@ant-design/icons';
import styled from 'styled-components';
import HTTPClient from "../../utils/HTTPClient";
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from "react-responsive";

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 27px;
    overflow: hidden;
  }
  
  .ant-modal-body {
    padding: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
`;

const IconPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  background-color: #d9d9d9;
  border-radius: 50%;
  margin-right: 15px;
`;

const HeaderText = styled.div`
  h2 {
    margin: 0;
    font-size: 18px;
  }
  p {
    margin: 5px 0 0;
    font-size: 14px;
    color: #666;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const Step = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#1890ff' : '#d9d9d9'};
  margin: 0 10px;
`;

const StepContent = styled.div`
  margin-bottom: 20px;
  h3 {
    margin-bottom: 10px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const StyledButton = styled(Button)`
  border-radius: 20px;
  margin: 0 10px;
`;

const CreateModal = ({ open, onCancel, setLoading, setFlowData, setShowCreateModal }) => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
    const [newFlowName, setNewFlowName] = useState("");

    const { t } = useTranslation();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const res = await HTTPClient.get("/ab_flow/templates");
            if (res.data) {
                setTemplates(res.data);
            }
            setLoading(false);
        };
        fetchData();
    }, []);

    const isLargeScreen = useMediaQuery({
        query: "(min-width: 771px)",
    });

    const handleMoreInfo = (item) => {
        setSelectedTemplate(item);
        setIsDetailModalVisible(true);
    };

    const closeOnCancel = () => {
        setNewFlowName("");
        setSelectedTemplate(null);
        onCancel();
    };

    const createFlow = async () => {
        setLoading(true);
        await HTTPClient.post("/ab_flow/flow", { name: newFlowName, template_id: selectedTemplate?.id });
        setNewFlowName("");
        setSelectedTemplate(null);
        setShowCreateModal(false);
        const res = await HTTPClient.get("/ab_flow/");
        const newFlowData = res.data;
        setFlowData(newFlowData);
        setLoading(false);
    };

    const DetailModalBody = (
        <ModalBody>
            {selectedTemplate && (
                <Row>
                    <Col span={12} style={{ padding: "10px" }}>
                        <Row>
                            <Col span={12} style={{ padding: "10px" }}>
                                <img src={selectedTemplate.image1} alt="Template" style={{ width: "100%" }} />
                            </Col>
                            <Col span={12} style={{ padding: "10px" }}>
                                <img src={selectedTemplate.image2} alt="Template" style={{ width: "100%" }} />
                            </Col>
                        </Row>
                    </Col>
                    <Col span={12} style={{ padding: "10px" }}>
                        <h2>{selectedTemplate.title}</h2>
                        <p>{selectedTemplate.description}</p>
                    </Col>
                </Row>)}
            <ButtonContainer>
                <StyledButton
                    type="primary"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => setIsDetailModalVisible(false)}
                >Back</StyledButton>
            </ButtonContainer>
        </ModalBody>
    );

    const ListModalBody = (
        <ModalBody>
            <Row>
                {t("ab_flow.create_modal.text1")}
            </Row>
            <div style={{ marginTop: "10px", maxHeight: "300px", overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: "4px" }}>
                <List
                    bordered
                    dataSource={templates}
                    renderItem={(item) => (
                        <List.Item
                            onClick={() => setSelectedTemplate(item)}
                            style={{
                                background: selectedTemplate && selectedTemplate.id === item.id ? "#e6f7ff" : "white",
                                cursor: "pointer",
                                padding: "12px",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingRight: "12px" }}>
                                <img
                                    src={item.image1}
                                    alt="Icona 1"
                                    style={{ width: "40px", height: "40px", borderRadius: "4px" }}
                                />
                                <img
                                    src={item.image2}
                                    alt="Icona 2"
                                    style={{ width: "40px", height: "40px", borderRadius: "4px" }}
                                />
                            </div>
                            <List.Item.Meta
                                title={item.title}
                                description={item.description.length > 50 ? item.description.substring(0, 70) + "..." : item.description}
                            />

                            <Button type="link" onClick={(e) => {
                                e.stopPropagation();
                                handleMoreInfo(item);
                            }}>
                                {t("ab_flow.create_modal.details")}
                            </Button>
                        </List.Item>
                    )}
                />
            </div>
            <Row style={{ marginTop: "20px" }}>
                {t("ab_flow.create_modal.text2")}
            </Row>
            <Row style={{ marginTop: "10px" }}>
                <Input value={newFlowName} onChange={(e) => setNewFlowName(e.target.value)} placeholder="Send Welcome Email" />
            </Row>
            <ButtonContainer>
                <StyledButton
                    type="primary"
                    onClick={() => createFlow()}
                    disabled={!newFlowName.length || !selectedTemplate}
                    icon={<ArrowRightOutlined />}
                >
                    {t("ab_flow.create_modal.button")}
                </StyledButton>
            </ButtonContainer>
        </ModalBody>
    );

    return (
        <StyledModal
            open={open}
            onCancel={closeOnCancel}
            footer={null}
            width={isLargeScreen ? "50%" : "100%"}
            title={<div style={{ fontSize: "24px" }}><SisternodeOutlined style={{ color: "#5F4DEE", fontSize: "28px", marginRight: "10px" }} />{t("ab_flow.create_modal.title")}</div>}
        >
            {isDetailModalVisible ? DetailModalBody : ListModalBody}
        </StyledModal>
    );
};

export { CreateModal };